import React, { FC, useState, useEffect } from "react";
import {
  IContract,
  IPayout,
  ICashFlowData,
  ICalculationParametersData,
} from "types";
import Button from "components/Button/Button";
import { ContractDetails } from "../ContractDetails/ContractDetails";

import { CashFlowsTable } from "../CashFlowsTable/CashFlowsTable";
import { CalculationParametersTable } from "../CalculationParametersTable/CalculationParametersTable";
import styles from "./ContractView.module.scss";
import { ContractPayouts } from "../ContractPayouts/ContractPayouts";
import { ReactComponent as CashIcon } from "icons/cash_icon.svg";
import { ReactComponent as StatIcon } from "icons/stat_icon.svg";
import { ReactComponent as CalcIcon } from "icons/calc_icon.svg";
import { ReactComponent as ExcelIcon } from "icons/excel.svg";
import { ReactComponent as CashIconActive } from "icons/cash_icon_active.svg";
import { ReactComponent as StatIconActive } from "icons/stat_icon_active.svg";
import { ReactComponent as CalcIconActive } from "icons/calc_icon_active.svg";

interface Props {
  contract: IContract;
  payouts?: IPayout[] | { calculations: IPayout[]; payments: IPayout[] };
  cashFlows?: ICashFlowData[];
  calculationParameters?: ICalculationParametersData[];
  onLoadPayouts: () => Promise<any>;
  onLoadCashFlows: () => Promise<any>;
  onLoadCalculationParameters: () => Promise<any>;
  onDownloadExcel: () => void;
  measure?: () => void;
}

enum ReportTab {
  Payouts = "payouts",
  CashFlows = "cashFlows",
  CalcParams = "calcParams",
}

const processServerData = (data: any) => {
  if (
    Array.isArray(data) &&
    data.length > 0 &&
    data[0].cashFlows &&
    data[0].cashFlows.CashFlow
  ) {
    const cashFlows = JSON.parse(data[0].cashFlows.CashFlow);
    return cashFlows;
  } else {
    return [];
  }
};

const processCalculationParametersData = (data: any) => {
  if (
    Array.isArray(data) &&
    data.length > 0 &&
    data[0].calculationParameters &&
    typeof data[0].calculationParameters.calculationParameters === "string"
  ) {
    const calculationParameters = JSON.parse(
      data[0].calculationParameters.calculationParameters
    );
    return calculationParameters;
  } else {
    return [];
  }
};

export const ContractView: FC<Props> = ({
  measure,
  contract,
  payouts,
  cashFlows,
  calculationParameters,
  onLoadPayouts,
  onLoadCashFlows,
  onLoadCalculationParameters,
  onDownloadExcel,
}) => {
  const [activeTab, setActiveTab] = useState<ReportTab | null>(null);
  const [loadingPayouts, setLoadingPayouts] = useState(false);
  const [loadedPayouts, setLoadedPayouts] = useState(false);
  const [loadingCashFlows, setLoadingCashFlows] = useState(false);
  const [loadedCashFlows, setLoadedCashFlows] = useState(false);
  const [loadingCalcParams, setLoadingCalcParams] = useState(false);
  const [loadedCalcParams, setLoadedCalcParams] = useState(false);

  const processedCashFlows = cashFlows ? processServerData(cashFlows) : [];

  const processedCalculationParameters = calculationParameters
    ? processCalculationParametersData(calculationParameters)
    : [];

  useEffect(() => {
  
  }, [payouts, cashFlows, calculationParameters, measure]);

  const handlePayoutsClick = async () => {
    if (!loadedPayouts) {
      setLoadingPayouts(true);
      await onLoadPayouts();
      setLoadedPayouts(true);
      setLoadingPayouts(false);
    }
    setActiveTab(ReportTab.Payouts);
  };

  const handleCashFlowsClick = async () => {
    if (!loadedCashFlows) {
      setLoadingCashFlows(true);
      await onLoadCashFlows();
      setLoadedCashFlows(true);
      setLoadingCashFlows(false);
    }
    setActiveTab(ReportTab.CashFlows);
  };
  

  const handleCalcParamsClick = async () => {
    if (!loadedCalcParams) {
      setLoadingCalcParams(true);
      await onLoadCalculationParameters();
      setLoadedCalcParams(true);
      setLoadingCalcParams(false);
    }
    setActiveTab(ReportTab.CalcParams);
  };

  return (
    <div className={styles.contract}>
      <div className={styles.info}>
        <ContractDetails {...contract} />
        
  <div className={styles.btns}>

  <div 
    className={`${styles.tabItem} ${
      ""
    }`}
    onClick={onDownloadExcel}
  >
    <ExcelIcon className={styles.icon} />
    <span className={styles.tabText}>Выгрузка в Excel</span>
  </div>

  <div 
    className={`${styles.tabItem} ${
      activeTab === ReportTab.Payouts ? styles.active : ""
    }`}
    onClick={handlePayoutsClick}
  >
    {activeTab === ReportTab.Payouts ? (
      <CashIconActive className={styles.icon} />
    ) : (
      <CashIcon className={styles.icon} />
    )}
    <span className={styles.tabText}>Выплаты начисленных %</span>
  </div>

  <div 
    className={`${styles.tabItem} ${
      activeTab === ReportTab.CashFlows ? styles.active : ""
    }`}
    onClick={handleCashFlowsClick}
  >
    {activeTab === ReportTab.CashFlows ? (
      <StatIconActive className={styles.icon} />
    ) : (
      <StatIcon className={styles.icon} />
    )}
    <span className={styles.tabText}>Пополнение / Отток ОД</span>
  </div>

  <div 
    className={`${styles.tabItem} ${
      activeTab === ReportTab.CalcParams ? styles.active : ""
    }`}
    onClick={handleCalcParamsClick}
  >
    {activeTab === ReportTab.CalcParams ? (
      <CalcIconActive className={styles.icon} />
    ) : (
      <CalcIcon className={styles.icon} />
    )}
    <span className={styles.tabText}>Процентная ставка</span>
  </div>
</div>

      </div>
      
      {activeTab === ReportTab.Payouts && loadedPayouts && payouts && (
  <ContractPayouts 
  payments={
    !Array.isArray(payouts) ? payouts.payments || [] : []
  }
    calculations={
      // Если payouts – массив, то считаем, что нужных данных нет, передаём пустой массив
      !Array.isArray(payouts) ? payouts.calculations || [] : []
    }
    
  />
)}

      {activeTab === ReportTab.CashFlows &&
        loadedCashFlows &&
        processedCashFlows && <CashFlowsTable data={processedCashFlows} />}

      {activeTab === ReportTab.CalcParams &&
        loadedCalcParams &&
        processedCalculationParameters && (
          <CalculationParametersTable data={processedCalculationParameters} />
        )}
    </div>
  );
};
